'use client';

import React from 'react';
import styled from 'styled-components';
import { Input } from '@/components/forms/controls/Input/Input';
import { Dropdown } from '@/components/forms/controls/Dropdown/Dropdown';
import { Button } from '@/components/ui-components/Button/Button';
import { TagInput } from '@/components/forms/controls/TagInput/TagInput';
import { Checkbox } from '@/components/forms/controls/Checkbox';
import { Tooltip } from '@/components/ui-components/Tooltip/Tooltip';
import { InfoTooltipIcon } from '@/components/icons/InfoTooltipIcon';
import { NumberInput } from '@/components/forms/controls/NumberInput/NumberInput';
import { TextArea } from '@/components/forms/controls/TextArea/TextArea';

/**
 * Pattern Form Props
 */
export interface PatternFormProps {
  /** Pattern name value */
  patternName: string;
  /** Pattern type value */
  patternType: string;
  /** Severity value */
  severity: string;
  /** Cooldown value in minutes */
  cooldown: number;
  /** Keywords array */
  keywords: string[];
  /** Search fields array */
  searchFields: string[];
  /** Email recipients string */
  emailRecipients: string;
  /** Active status */
  isActive: boolean;
  /** Description text */
  description: string;
  
  /** Handlers */
  onPatternNameChange: (value: string) => void;
  onPatternTypeChange: (value: string) => void;
  onSeverityChange: (value: string) => void;
  onCooldownChange: (value: number) => void;
  onKeywordsChange: (value: string[]) => void;
  onSearchFieldsChange: (value: string[]) => void;
  onEmailRecipientsChange: (value: string) => void;
  onIsActiveChange: (value: boolean) => void;
  onDescriptionChange: (value: string) => void;
  
  /** Form submission */
  onSubmit: () => void;
  onClear?: () => void;
  
  /** Optional custom styling */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const FormContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const FormTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 24px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 24px;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * PatternForm Component
 * 
 * A reusable form component for creating/editing alert patterns.
 * Handles all form fields and validation for pattern configuration.
 * 
 * @component
 * @example
 * ```tsx
 * <PatternForm
 *   patternName={patternName}
 *   onPatternNameChange={setPatternName}
 *   // ... other props
 *   onSubmit={handleSubmit}
 * />
 * ```
 */
export const PatternForm: React.FC<PatternFormProps> = ({
  patternName,
  patternType,
  severity,
  cooldown,
  keywords,
  searchFields,
  emailRecipients,
  isActive,
  description,
  onPatternNameChange,
  onPatternTypeChange,
  onSeverityChange,
  onCooldownChange,
  onKeywordsChange,
  onSearchFieldsChange,
  onEmailRecipientsChange,
  onIsActiveChange,
  onDescriptionChange,
  onSubmit,
  onClear,
  className,
}) => {
  const patternTypeOptions = [
    { label: 'Keyword', value: 'keyword' },
    { label: 'Regex', value: 'regex' },
    { label: 'Frequency', value: 'frequency' },
  ];
  
  const severityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Critical', value: 'critical' },
  ];
  
  const searchFieldOptions = [
    { label: 'Basic', value: 'basic' },
    { label: 'Decoded Response', value: 'decoded_records' },
    { label: 'Headers', value: 'headers' },
    { label: 'Query Parameters', value: 'query_params' },
    { label: 'Pattern Type', value: 'pattern_type' },
    { label: 'Alert Severity', value: 'alert_severity' },
  ];

  return (
    <FormContainer className={className}>
      <FormCard>
        <FormTitle>Create New Alert Pattern</FormTitle>
        
        <FormGrid>
          <FormField>
            <FieldLabel>
              Pattern Name
            </FieldLabel>
            <Input
              placeholder="e.g., Critical Error Alert"
              value={patternName}
              onChange={(e) => onPatternNameChange(e.target.value)}
              fullWidth
            />
          </FormField>
          
          <FormField>
            <FieldLabel>
              Search In Fields
              <Tooltip title="Select which fields to search for patterns" theme="light">
                <InfoTooltipIcon size={16} color="#000000" />
              </Tooltip>
            </FieldLabel>
            <TagInput
              label=""
              placeholder="Select fields..."
              value={searchFields}
              options={searchFieldOptions}
              onChange={onSearchFieldsChange}
            />
          </FormField>
          
          <FormField>
            <FieldLabel>
              Pattern Type
              <Tooltip title="Choose how to match patterns in logs" theme="light">
                <InfoTooltipIcon size={16} color="#000000" />
              </Tooltip>
            </FieldLabel>
            <Dropdown
              options={patternTypeOptions}
              value={patternType}
              onChange={onPatternTypeChange}
            />
          </FormField>
          
          <FormField>
            <FieldLabel>
              Email Recipients
              <Tooltip title="Comma-separated email addresses" theme="light">
                <InfoTooltipIcon size={16} color="#000000" />
              </Tooltip>
            </FieldLabel>
            <Input
              placeholder="email1@company.com, email2@company.com"
              value={emailRecipients}
              onChange={(e) => onEmailRecipientsChange(e.target.value)}
              fullWidth
            />
          </FormField>
          
          <FormField>
            <FieldLabel>
              Alert Severity
            </FieldLabel>
            <Dropdown
              options={severityOptions}
              value={severity}
              onChange={onSeverityChange}
            />
          </FormField>
          
          <FormField>
            <NumberInput
              label="Cooldown (minutes)"
              value={cooldown}
              onChange={onCooldownChange}
              min={1}
              step={5}
              helperIcon={
                <Tooltip title="Minimum time between alerts for the same pattern" theme="light">
                  <InfoTooltipIcon size={16} color="#000000" />
                </Tooltip>
              }
            />
          </FormField>
          
          <FormField $fullWidth>
            <FieldLabel>
              Keywords (comma-separated)
            </FieldLabel>
            <Input
              placeholder="error, failed, exception"
              value={keywords.join(', ')}
              onChange={(e) => onKeywordsChange(e.target.value?.split(',').map(k => k?.trim()).filter(k => k))}
              fullWidth
            />
          </FormField>
          
          <FormField $fullWidth>
            <TextArea
              label="Description (optional)"
              value={description}
              onChange={onDescriptionChange}
              placeholder="What this pattern detects..."
              rows={5}
            />
          </FormField>
          
          <FormField $fullWidth>
            <Checkbox
              label="Active"
              checked={isActive}
              onChange={onIsActiveChange}
              helpText="Enable or disable this alert pattern"
            />
          </FormField>
        </FormGrid>
        
        <ButtonContainer>
          <Button variant="primary" size="medium" onClick={onSubmit}>
            Create Pattern
          </Button>
          {onClear && (
            <Button variant="outline" size="medium" onClick={onClear}>
              Clear Form
            </Button>
          )}
        </ButtonContainer>
      </FormCard>
    </FormContainer>
  );
};

export default PatternForm;

